import { useEffect, useState } from "react";
import { apiService } from "../../utils/apiService";
import LoadingSection from "../LoadingSection";
import ErrorComponent from "../Error";
import { selectAccountData } from "../../redux/main/accountSlice";
import Loader from "../Loader";
import {
    getServiceDetails,
    selectServiceDetailsData,
    selectServiceDetailsError,
    selectServiceDetailsIsLoading
} from "../../redux/services/serviceDetailsSlice";
import {
    setGoingToClose,
    setHasToRefresh
} from "../../redux/rightModalSlice";
import { useDispatch, useSelector } from "react-redux";

export default function AddEditService({ serviceId }) {
    // Chiamata iniziale per ricevere i dettagli del servizio
    const accountData = useSelector(selectAccountData);
    const token = localStorage.getItem('installamelo-auth-token') ?? '';
    const [serviceDataInput, setServiceDataInput] = useState({});
    const [unitTypes, setUnitTypes] = useState([]);
    const [vatTypes, setVatTypes] = useState([]);
    const [isLoadingSettings, setIsLoadingSettings] = useState(true);
    const [settingsError, setSettingsError] = useState('');
    
    // Carico le impostazioni dell'account (tipi di unità e tipi IVA)
    useEffect(() => {
        const fetchSettings = async () => {
            if (accountData) {
                try {
                    setIsLoadingSettings(true);
                    const url = `${import.meta.env.VITE_API_BASE_URL}/accounts/settings?accountId=${accountData._id}`;
                    const response = await fetch(url, {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            'auth-token': token
                        }
                    });
                    
                    if (!response.ok) {
                        throw new Error("Errore nel caricamento delle impostazioni");
                    }
                    
                    const settings = await response.json();
                    
                    // Imposto i tipi di unità
                    if (settings.servicesUnitType && settings.servicesUnitType.length > 0) {
                        setUnitTypes(settings.servicesUnitType);
                    } else {
                        // Fallback ai valori di default se non ci sono impostazioni
                        setUnitTypes([
                            { _id: "hour", label: "Ora", name: "Ora" },
                            { _id: "day", label: "Giorno", name: "Giorno" },
                            { _id: "piece", label: "Pezzo", name: "Pezzo" }
                        ]);
                    }
                    
                    // Imposto i tipi IVA
                    if (settings.vatTypes && settings.vatTypes.length > 0) {
                        setVatTypes(settings.vatTypes);
                    } else {
                        // Fallback ai valori di default se non ci sono impostazioni
                        setVatTypes([
                            { _id: "standard", label: "Standard (22%)", value: 22 },
                            { _id: "reduced", label: "Ridotta (10%)", value: 10 },
                            { _id: "superReduced", label: "Super-ridotta (4%)", value: 4 },
                            { _id: "exempt", label: "Esente", value: 0 }
                        ]);
                    }
                    
                    // Imposto accountId nel serviceDataInput
                    setServiceDataInput((prevState) => ({
                        ...prevState,
                        accountId: accountData._id
                    }));
                    
                    setIsLoadingSettings(false);
                } catch (error) {
                    console.error("Errore nel caricamento delle impostazioni:", error);
                    setSettingsError(error.message);
                    setIsLoadingSettings(false);
                    
                    // Imposto valori di default in caso di errore
                    setUnitTypes([
                        { _id: "hour", label: "Ora", name: "Ora" },
                        { _id: "day", label: "Giorno", name: "Giorno" },
                        { _id: "piece", label: "Pezzo", name: "Pezzo" }
                    ]);
                    
                    setVatTypes([
                        { _id: "standard", label: "Standard (22%)", value: 22 },
                        { _id: "reduced", label: "Ridotta (10%)", value: 10 },
                        { _id: "superReduced", label: "Super-ridotta (4%)", value: 4 },
                        { _id: "exempt", label: "Esente", value: 0 }
                    ]);
                    
                    // Imposto accountId nel serviceDataInput anche in caso di errore
                    setServiceDataInput((prevState) => ({
                        ...prevState,
                        accountId: accountData._id
                    }));
                }
            }
        };
        
        fetchSettings();
    }, [accountData, token]);
    
    // Imposto valori di default quando si crea un nuovo servizio
    useEffect(() => {
        // Solo per nuovi servizi (quando serviceId non è presente)
        if (!serviceId && unitTypes.length > 0 && vatTypes.length > 0) {
            // Trovo il tipo IVA di default o uso il primo
            const defaultVatType = vatTypes.find(type => type.default) || vatTypes[0];
            
            // Per il tipo unità uso il primo elemento dell'array
            const defaultUnitType = unitTypes[0];
            
            setServiceDataInput((prevState) => ({
                // Mantengo solo accountId e resetto tutti gli altri campi
                accountId: prevState.accountId,
                // Pulisco tutti i campi di input
                name: "",
                time: 0,
                code: "",
                vatId: defaultVatType._id,
                vatValue: defaultVatType.value,
                vatLabel: defaultVatType.label,
                unitTypeId: defaultUnitType._id,
                unitTypeLabel: defaultUnitType.name,
                unitTypeKind: defaultUnitType._id
            }));
        }
    }, [serviceId, unitTypes, vatTypes]);
    
    const dispatch = useDispatch();
    const serviceDetailsData = useSelector(selectServiceDetailsData);
    const serviceDetailsIsLoading = useSelector(selectServiceDetailsIsLoading);
    const serviceDetailsError = useSelector(selectServiceDetailsError);
    
    useEffect(() => {
        if (serviceId) dispatch(getServiceDetails({ serviceId, token }))
    }, [serviceId, dispatch, token])
    
    // Ricevuti i dati servizio da Redux imposto il serviceDataInput per la modifica
    useEffect(() => {
        if (serviceDetailsData) {
            // Manteniamo il unitTypeId originale del servizio
            setServiceDataInput((prevState) => ({
                ...prevState,
                ...serviceDetailsData
            }))
        }
    }, [serviceDetailsData])

    // Funzione generica per aggiornare il serviceDataInput
    const handleServiceDataInput = (event) => {
        const { id, value } = event.target;
        setServiceDataInput((prevState) => ({
            ...prevState,
            [id]: value
        }))
    }
    
    // Funzione per gestire il cambio di tipo unità
    const handleUnitTypeChange = (event) => {
        const selectedUnitType = unitTypes.find(type => type._id === event.target.value);
        if (selectedUnitType) {
            setServiceDataInput((prevState) => ({
                ...prevState,
                unitTypeId: selectedUnitType._id,
                unitTypeLabel: selectedUnitType.name,
                unitTypeKind: selectedUnitType._id
            }));
        }
    };
    
    // Funzione per gestire il cambio di tipo IVA
    const handleVatTypeChange = (event) => {
        const selectedVatType = vatTypes.find(type => type._id === event.target.value);
        if (selectedVatType) {
            setServiceDataInput((prevState) => ({
                ...prevState,
                vatId: selectedVatType._id,
                vatLabel: selectedVatType.label,
                vatValue: selectedVatType.value
            }));
        }
    };
    
    // Funzione per validare i dati prima dell'invio
    const validateServiceData = () => {
        // Verifica che i campi obbligatori siano compilati
        if (!serviceDataInput.name) {
            setSendError("Nome è un campo obbligatorio");
            setSendFetchStatus('f'); // Imposto lo stato a 'f' (failure) per mantenere i bottoni visibili
            return false;
        }
        
        // Verifica che il tipo unità e IVA siano selezionati
        if (!serviceDataInput.unitTypeId || !serviceDataInput.vatId) {
            setSendError("Tipo unità e IVA sono campi obbligatori");
            setSendFetchStatus('f'); // Imposto lo stato a 'f' (failure) per mantenere i bottoni visibili
            return false;
        }
        
        return true;
    };

    const [sendFetchStatus, setSendFetchStatus] = useState('i');
    const [sendError, setSendError] = useState('');
    
    const sendServiceData = async () => {
        // Validazione dei dati
        if (!validateServiceData()) {
            // Non cambiamo sendFetchStatus, mostriamo solo l'errore
            return;
        }
        
        setSendFetchStatus('l');
        const url = `${import.meta.env.VITE_API_BASE_URL}/services`;
        
        try {
            // Preparo l'oggetto da inviare
            const serviceToSend = {
                service: serviceDataInput
            };
            
            // Utilizzo fetch direttamente
            const response = await fetch(url, {
                method: serviceId ? 'PATCH' : 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': token
                },
                body: JSON.stringify(serviceToSend)
            });
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Errore da parte del server.");
            }
            
            const resultData = await response.json();
            
            console.log('resultData: ', resultData);
            setSendFetchStatus('s');
            // Resetto eventuali errori precedenti quando l'operazione va a buon fine
            setSendError('');
            dispatch(setHasToRefresh());
            
            // Chiudo la modale dopo 1.5 secondi per mostrare il messaggio di successo
            setTimeout(() => {
                dispatch(setGoingToClose());
            }, 1500);
        } catch (error) {
            console.error(error.message || "Errore da parte del server.");
            setSendFetchStatus('f');
            setSendError(error.message || "Errore da parte del server.");
        }
    }
    
    return (
        <div className="w-full p-8">
            {
                serviceDetailsIsLoading && <LoadingSection isFixed={false} />
            }
            {
                serviceDetailsError && <ErrorComponent message={serviceDetailsError} />
            }
            {
                (serviceDetailsData || !serviceId) &&
                <div className="flex flex-col gap-4 mt-8 border-2 rounded-lg p-4 border-blue-200 relative">
                    <h1>{serviceId ? 'Modifica Servizio' : 'Nuovo Servizio'}</h1>
                    {serviceId && <div className="light-text mt-[-24px]">Id servizio: {serviceId}</div>}
                    
                    {/* INFORMAZIONI */}
                    <h2>Informazioni</h2>
                    
                    {/* Nome e Tempo */}
                    <div className="flex gap-2">
                        <div className="input-box flex-1">
                            <label htmlFor="name">Nome <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                id="name"
                                value={serviceDataInput?.name || ""}
                                onChange={handleServiceDataInput}
                            />
                        </div>
                        <div className="input-box flex-1">
                            <label htmlFor="time">Tempo (minuti)</label>
                            <input
                                type="number"
                                id="time"
                                value={serviceDataInput?.time || 0}
                                onChange={handleServiceDataInput}
                            />
                        </div>
                    </div>

                    {/* Prezzo */}
                    <div className="flex gap-2">
                        <div className="input-box flex-1">
                            <label htmlFor="price">Prezzo (EUR)</label>
                            <input
                                type="number"
                                id="price"
                                step="0.01"
                                min="0"
                                value={serviceDataInput?.price || ""}
                                onChange={handleServiceDataInput}
                                placeholder="0.00"
                            />
                        </div>
                        <div className="flex-1"></div>
                    </div>
                    
                    {/* Tipo Unità e IVA */}
                    <div className="flex gap-2">
                        <div className="input-box flex-1">
                            <label htmlFor="unitTypeId">Tipo Unità <span className="text-red-500">*</span></label>
                            <select
                                id="unitTypeId"
                                value={serviceDataInput?.unitTypeId || ""}
                                onChange={handleUnitTypeChange}
                                className="w-full p-2 border rounded"
                                required
                            >
                                <option value="">Seleziona un tipo unità</option>
                                {unitTypes.map(type => (
                                    <option key={type._id} value={type._id}>
                                        {type.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="input-box flex-1">
                            <label htmlFor="vatId">IVA <span className="text-red-500">*</span></label>
                            <select
                                id="vatId"
                                value={serviceDataInput?.vatId || ""}
                                onChange={handleVatTypeChange}
                                className="w-full p-2 border rounded"
                                required
                            >
                                <option value="">Seleziona un tipo IVA</option>
                                {vatTypes.map(type => (
                                    <option key={type._id} value={type._id}>
                                        {type.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    
                    {/* Messaggio di errore con sfondo rosso trasparente */}
                    {sendError && (
                        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                            {sendError}
                        </div>
                    )}
                    
                    <div className="flex gap-2 h-[40px]">
                        {/* Mostra i bottoni sempre tranne quando è in caricamento o successo senza errori */}
                        {
                            (sendFetchStatus !== 'l' && sendFetchStatus !== 's') || sendError ?
                            <div className="flex gap-2">
                                <button className="primary" onClick={sendServiceData}>{serviceId ? 'Salva Servizio' : 'Crea Nuovo Servizio'}</button>
                                <button className="secondary" onClick={() => dispatch(setGoingToClose())}>Annulla</button>
                            </div>
                            : null
                        }
                        {
                            sendFetchStatus === 'l' && !sendError &&
                            <Loader />
                        }
                        {
                            sendFetchStatus === 's' && !sendError &&
                            <h3>Servizio {serviceId ? 'aggiornato' : 'creato'} con successo.</h3>
                        }
                    </div>
                </div>
            }
        </div>
    )
}
